// ** React Imports
import { Fragment, useState, useRef, useEffect } from "react";

// ** Third Party Components
import classnames from "classnames";
import PerfectScrollbar from "react-perfect-scrollbar";

// ** Vertical Menu Components
import VerticalMenuHeader from "./VerticalMenuHeader";
import VerticalNavMenuItems from "./VerticalNavMenuItems";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@src/redux";
import { replaceLongStringToPoints } from "@src/utility/StringUtil";
import { Account, PlanType, Subscription, UserAccount } from "@src/@types/UserAccount";
import { handleSetActiveAccount } from "@src/redux/reducers/userAccount";
import { Site } from "@src/@types/SitesTypes";
import { useTranslation } from "react-i18next";
import { AuthUser } from "@src/@types/AuthUser";
import { checkIfIsValidEmail } from "@src/utility/GeneralUtil";
import { Button } from "reactstrap";
import { ArrowRight } from "react-feather";
import { useNavigate } from "react-router-dom";
import SetaBaixo from "src/assets/images/pages/setaparabaixo.svg";
import UserDropdown from "../../navbar/UserDropdown";
import "src/@core/scss/base/components/sidebar-account-dropdown.scss";

interface VerticalMenu {
  menuCollapsed: any;
  menu: any; 
  skin: any;
  menuData: any;
  menuVisibility: any;
  setMenuCollapsed: Function;
  setMenuVisibility: Function;
}
const Sidebar = ({ menuCollapsed, menu, skin, menuData,menuVisibility,setMenuCollapsed,setMenuVisibility }:VerticalMenu) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const subscription = useSelector<RootState, Subscription>(
    (state) => state.userAccount.subscription as Subscription
  );
  const accountUser:UserAccount = useSelector<RootState, UserAccount>((state) => state.userAccount.userAccount);
  const activeAccount = useSelector<RootState, Account | undefined>((state) => state.userAccount.activeAccount);
  const authUser:AuthUser = useSelector<RootState, AuthUser>((state) => state.authUser.authUser);
  const sites = useSelector<RootState, Site[]>((state) => state.userAccount.sites);
  // ** States
  const [groupOpen, setGroupOpen] = useState([]);
  const [groupActive, setGroupActive] = useState([]);
  const [currentActiveGroup, setCurrentActiveGroup] = useState([]);
  const [activeItem, setActiveItem] = useState(null);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);

  // ** Menu Hover State
  const [menuHover, setMenuHover] = useState(false);

  // ** Refs
  const shadowRef = useRef<HTMLDivElement>(null);
  const accountDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target as Node)) {
        setIsAccountDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ** Function to handle Mouse Enter
  const onMouseEnter = () => {
    setMenuHover(true);
  };

  // ** Scroll Menu
  const scrollMenu = (container: any) => {
    if (shadowRef && container.scrollTop > 0) {
      if (shadowRef.current && !shadowRef.current.classList.contains("d-block")) {
        shadowRef.current.classList.add("d-block");
      }
    } else {
      if (shadowRef.current && shadowRef.current.classList.contains("d-block")) {
        shadowRef.current.classList.remove("d-block");
      }
    }
  };
  const getAccountName = () => {
    let firstAccount = accountUser.accounts;
    if(firstAccount && firstAccount[0]  && !checkIfIsValidEmail(firstAccount[0].name)){
      let name = firstAccount[0].name;
      return name;
    }
    return authUser.fullName?.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  const getAccountDisplayName = (account: Account) => {
    return account.astvId ? `${account.astvId} - ${account.name ?? ''}` : (account.name ?? '');
  };

  const getSiteText = () => {
    let totalActiveSites = sites.filter(s => s.enabled);
    if(totalActiveSites?.length > 0 ){
      if( totalActiveSites?.length > 1 ){
        return  totalActiveSites?.length + " " + t("menu.activeSites");
      }
      return '1 '+t("menu.activeSite");
    } 
    return '0 '+t("menu.activeSites");
  }

  // LOGICA PARA SABER SE O MENU ESTA EXPANDIDO
  const isExpanded = menuHover || !menuCollapsed;

  return (
    <Fragment>
      <div
        className={classnames(
          "main-menu menu-fixed menu-accordion menu-shadow menu-padding",
          {
            expanded: isExpanded, // Usando a variavel que criamos aqui para ficar mais limpo
            "menu-light": skin !== "semi-dark" && skin !== "dark",
            "menu-dark": skin === "semi-dark" || skin === "dark",
          }
        )}
        //onMouseEnter={onMouseEnter}
        //onMouseLeave={() => setMenuHover(false)}
      >
        {menu ? (
          menu({menuCollapsed, menu, skin, menuData, setMenuCollapsed })
        ) : (
          <Fragment>
            <div className="menu-secundary">
              {/* Vertical Menu Header */}
              <VerticalMenuHeader
                setMenuCollapsed={setMenuCollapsed}
                  setMenuVisibility={setMenuVisibility}
                  setGroupOpen={setGroupOpen}
                  menuHover={menuHover} 
                menuCollapsed={menuCollapsed}
              />
              <div className="menu-secundary-body">
                { /* Seletor de conta */ }
                {accountUser?.accounts && accountUser.accounts.length > 1 && (
                  <div className="menu-account-wrapper" ref={accountDropdownRef}>
                    <div
                      className="menu-secundary-account-title"
                      tabIndex={0}
                      role="button"
                      onClick={() => setIsAccountDropdownOpen(prev => !prev)}
                      style={!isExpanded ? { justifyContent: 'center' } : {}}
                    >
                      {isExpanded && (
                        <h4 className="menu-account-title-text" title={activeAccount ? getAccountDisplayName(activeAccount) : getAccountName()}>
                          {activeAccount ? getAccountDisplayName(activeAccount) : getAccountName() || "Meta"}
                        </h4>
                      )}
                      <img
                        className="menu-account-title-arrow"
                        src={SetaBaixo as string}
                        style={{ transform: isAccountDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                      />
                    </div>

                    {isAccountDropdownOpen && (
                      <div
                        className={`menu-account-dropdown${!isExpanded ? ' collapsed' : ''}`}
                        style={!isExpanded ? { width: 'auto', minWidth: 'fit-content' } : {}}
                      >
                        {[...accountUser.accounts].sort((a, b) => (a.astvId ?? 0) - (b.astvId ?? 0)).map((account, index) => (
                          <Fragment key={account.astvId}>
                            <div
                              className={`menu-account-dropdown-item${account.astvId === activeAccount?.astvId ? ' active' : ''}`}
                              role="button"
                              tabIndex={0}
                              title={getAccountDisplayName(account)}
                              onClick={() => {
                                dispatch(handleSetActiveAccount(account));
                                setIsAccountDropdownOpen(false);
                              }}
                            >
                              <span>{isExpanded ? getAccountDisplayName(account) : String(account.astvId ?? '')}</span>
                            </div>
                          </Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <ul className="menu-secundary-abas">
                    <VerticalNavMenuItems
                      items={menuData}
                      menuData={menuData}
                      menuHover={menuHover}
                      groupOpen={groupOpen}
                      activeItem={activeItem}
                      groupActive={groupActive}
                      setGroupOpen={setGroupOpen}
                      menuCollapsed={menuCollapsed}
                      setActiveItem={setActiveItem}
                      setGroupActive={setGroupActive}
                      currentActiveGroup={currentActiveGroup}
                      setCurrentActiveGroup={setCurrentActiveGroup}
                    />
                </ul>
              </div>
  
              <UserDropdown isExpanded={isExpanded} />
              
              {/*
              <div className="shadow-bottom" ref={shadowRef}></div>
              
              <div>
              <PerfectScrollbar
                className="main-menu-content"
                options={{ wheelPropagation: false }}
                onScrollY={(container) => scrollMenu(container)}
              >
                <div className="divider my-2">
                <div className="divider-text"></div>
              </div> 
                <ul className="navigation navigation-main">
                  <VerticalNavMenuItems
                    items={menuData}
                    menuData={menuData}
                    menuHover={menuHover}
                    groupOpen={groupOpen}
                    activeItem={activeItem}
                    groupActive={groupActive}
                    setGroupOpen={setGroupOpen}
                    menuCollapsed={menuCollapsed}
                    setActiveItem={setActiveItem}
                    setGroupActive={setGroupActive}
                    currentActiveGroup={currentActiveGroup}
                    setCurrentActiveGroup={setCurrentActiveGroup}
                  />
                </ul>
                

              </PerfectScrollbar> 
              
                
                <div className="side-bar-menu-footer">
                  <div style={{padding: '15px'}}  hidden={subscription?.planType === PlanType.ENTERPRISE}>
                  
                    
                    <Button 
                    title={subscription?.planType === PlanType.TRIAL ?  'Ativar licença': subscription.status === 'EXPIRED'? 'Ativar licença': 'Upgrade'}
                    block 
                    color={"primary assistive-btn " +(!menuCollapsed || menuHover ? '': ' btn-icon ') } 
                    onClick={() => {navigate('plan')}} >
                    { !menuCollapsed || menuHover? subscription?.planType === PlanType.TRIAL ?  'Ativar licença': subscription.status === 'EXPIRED'? 'Ativar licença': 'Upgrade' : '' }
                        <ArrowRight size={16} />
                    </Button>
                    
                  </div>
                  
                  <hr className=""></hr>
                  <div style={{marginLeft:'10px'}}>
                      <strong>Assistive:</strong><br></br>
                      <i><span>"{t("menu.peopleUnique")}"</span></i>
                  </div>
                  
                </div>
                </div>
                */}
              
               
            </div>        
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default Sidebar;